import { useEffect, useRef } from "react";

/**
 * useOncePerKeyEffect
 * Runs the provided effect only once per distinct key, even under React 18 StrictMode
 * where effects may run twice in development. When the key changes, the effect runs again.
 *
 * @param {string} key - A stable string representing the logical identity of the effect
 * @param {() => (void | (() => void))} effect - Effect callback; may return a cleanup function
 * @param {any[]} deps - Additional dependencies that should trigger effect when changed
 */
export default function useOncePerKeyEffect(key, effect, deps = []) {
  const lastKeyRef = useRef(null);

  useEffect(() => {
    let cleanupFn = undefined;

    if (lastKeyRef.current === key) {
      return () => {
        if (typeof cleanupFn === "function") cleanupFn();
      };
    }

    lastKeyRef.current = key;

    const maybeCleanup = effect();
    if (typeof maybeCleanup === "function") {
      cleanupFn = maybeCleanup;
    }

    return () => {
      if (typeof cleanupFn === "function") cleanupFn();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ...deps]);
}
